import { useState, useCallback, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import {
  Download,
  ChevronDown,
  FileText,
  FileType,
  FileDown,
  Archive,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { API_BASE } from "../lib/api";

export interface DownloadPanelProps {
  taskId: string;
  drugName: string;
  hasLlmReport: boolean;
  disabled?: boolean;
  onDownloadStart?: (type: string) => void;
  onDownloadComplete?: (type: string) => void;
  onError?: (error: Error) => void;
}

type DownloadType = "llm" | "docx" | "standard" | "sources";
type DownloadStatus = "idle" | "loading" | "success" | "error";

interface DownloadItemState {
  type: DownloadType;
  status: DownloadStatus;
}

const DOWNLOAD_OPTIONS = [
  {
    id: "llm" as DownloadType,
    label: "AI 整合报告",
    format: ".md",
    icon: FileText,
    description: "LLM 生成的智能分析报告",
    requiresLlmReport: true,
  },
  {
    id: "pdf" as const,
    label: "PDF 文档",
    format: ".pdf",
    icon: FileType,
    description: "即将推出",
    disabled: true,
  },
  {
    id: "docx" as DownloadType,
    label: "Word 文档",
    format: ".docx",
    icon: FileDown,
    description: "LLM 分析报告 Word 版本",
  },
  { id: "divider-1" as const, isDivider: true },
  {
    id: "standard" as DownloadType,
    label: "标准报告",
    format: ".md/.txt",
    icon: FileText,
    description: "自动生成的结构化报告",
  },
  { id: "divider-2" as const, isDivider: true },
  {
    id: "sources" as DownloadType,
    label: "完整数据包",
    format: ".zip",
    icon: Archive,
    description: "所有数据源原始数据",
  },
];

function getDownloadUrl(type: DownloadType, taskId: string): string {
  switch (type) {
    case "llm":
      return `${API_BASE}/tasks/${taskId}/report/llm`;
    case "docx":
      return `${API_BASE}/tasks/${taskId}/report`;
    case "standard":
      return `${API_BASE}/tasks/${taskId}/report/standard`;
    case "sources":
      return `${API_BASE}/tasks/${taskId}/download/sources`;
    default:
      return "";
  }
}

function getFilename(type: DownloadType, drugName: string): string {
  const safe = drugName.replace(/[^a-zA-Z0-9_\-\u4e00-\u9fff]/g, "_");
  switch (type) {
    case "llm":
      return `${safe}_AI_Report.md`;
    case "docx":
      return `${safe}_报告.docx`;
    case "standard":
      return `${safe}_Standard_Report.md`;
    case "sources":
      return `${safe}_sources.zip`;
    default:
      return `download_${type}`;
  }
}

function StatusIcon({ status }: { status: DownloadStatus }) {
  if (status === "loading") {
    return <Loader2 className="w-4 h-4 animate-spin text-primary" />;
  }
  if (status === "success") {
    return <CheckCircle2 className="w-4 h-4 text-success" />;
  }
  if (status === "error") {
    return <XCircle className="w-4 h-4 text-destructive" />;
  }
  return null;
}

function DownloadButton({
  option,
  taskId,
  drugName,
  itemState,
  disabled,
  onDownloadStart,
  onDownloadComplete,
  onError,
}: {
  option: (typeof DOWNLOAD_OPTIONS)[number];
  taskId: string;
  drugName: string;
  itemState: DownloadItemState | null;
  disabled: boolean;
  onDownloadStart?: (type: string) => void;
  onDownloadComplete?: (type: string) => void;
  onError?: (error: Error) => void;
}) {
  if ("isDivider" in option) {
    return <div className="my-1 h-px bg-border" />;
  }

  const isDisabled = disabled || !!option.disabled || ("requiresLlmReport" in option && false);
  const Icon = option.icon;

  if (option.disabled || ("id" in option && option.id === "pdf")) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-not-allowed opacity-50",
          "text-muted-foreground"
        )}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{option.label}</span>
            <span className="text-xs text-muted-foreground">({option.format})</span>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">即将推出</span>
      </div>
    );
  }

  const handleDownload = async () => {
    if (!("id" in option) || isDisabled || itemState?.status === "loading") return;
    const typeId = option.id as DownloadType;
    onDownloadStart?.(typeId);

    try {
      const url = getDownloadUrl(typeId, taskId);
      const filename = getFilename(typeId, drugName);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      onDownloadComplete?.(typeId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error(`Download failed (${option.label}):`, error);
      onError?.(error);
    }
  };

  const isActive = itemState?.status === "loading" || itemState?.status === "success" || itemState?.status === "error";
  const statusLabel: Record<DownloadStatus, string> = {
    idle: "下载",
    loading: "下载中...",
    success: "已下载",
    error: "重试",
  };

  return (
    <button
      onClick={handleDownload}
      disabled={isDisabled}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-left transition-colors duration-150",
        "hover:bg-muted/80 active:bg-muted",
        isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        isActive && itemState?.status === "error" && "bg-destructive/5"
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0 text-foreground/70" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{option.label}</span>
          <span className="text-xs text-muted-foreground">({option.format})</span>
        </div>
        {"description" in option && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{option.description}</p>
        )}
      </div>
      {isActive ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <StatusIcon status={itemState!.status} />
          <span
            className={cn(
              "text-xs font-medium",
              itemState!.status === "success" && "text-success",
              itemState!.status === "error" && "text-destructive",
              itemState!.status === "loading" && "text-primary"
            )}
          >
            {statusLabel[itemState!.status]}
          </span>
        </div>
      ) : (
        !isDisabled && (
          <span className="text-xs font-medium text-primary flex-shrink-0">下载</span>
        )
      )}
    </button>
  );
}

export default function DownloadPanel({
  taskId,
  drugName,
  hasLlmReport,
  disabled = false,
  onDownloadStart,
  onDownloadComplete,
  onError,
}: DownloadPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [downloadStates, setDownloadStates] = useState<Record<string, DownloadItemState>>({});
  const panelRef = useRef<HTMLDivElement>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleDownloadStart = useCallback((type: string) => {
    setDownloadStates(prev => ({ ...prev, [type]: { type: type as DownloadType, status: "loading" } }));
    onDownloadStart?.(type);
  }, [onDownloadStart]);

  const handleDownloadComplete = useCallback((type: string) => {
    setDownloadStates(prev => ({ ...prev, [type]: { type: type as DownloadType, status: "success" } }));
    onDownloadComplete?.(type);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => {
      setDownloadStates(prev => {
        const next = { ...prev };
        delete next[type];
        return next;
      });
    }, 2500);
  }, [onDownloadComplete]);

  const handleError = useCallback((error: Error) => {
    onError?.(error);
  }, [onError]);

  const anyLoading = Object.values(downloadStates).some(s => s.status === "loading");

  return (
    <div ref={panelRef} className="relative inline-block dropdown-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "btn btn-primary gap-2",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        {anyLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>下载中...</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>下载报告</span>
            <ChevronDown
              className={cn(
                "w-3.5 h-3.5 transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 top-full mt-2 w-72 z-[9999]",
            "bg-card border border-border rounded-lg shadow-lg overflow-hidden",
            "animate-fadeIn origin-top-right"
          )}
          style={{ animationDuration: "150ms" }}
        >
          <div className="p-2 space-y-0.5">
            {DOWNLOAD_OPTIONS.map(option => {
              if ("isDivider" in option) {
                return <div key={option.id} className="my-1 h-px bg-border" />;
              }

              const opt = option as Extract<typeof DOWNLOAD_OPTIONS[number], { id: string }>;
              const itemId = opt.id;
              const itemState = downloadStates[itemId] || null;

              const shouldHide =
                "requiresLlmReport" in option &&
                option.requiresLlmReport &&
                !hasLlmReport;

              if (shouldHide) return null;

              return (
                <DownloadButton
                  key={option.id || itemId}
                  option={option}
                  taskId={taskId}
                  drugName={drugName}
                  itemState={itemState}
                  disabled={disabled}
                  onDownloadStart={handleDownloadStart}
                  onDownloadComplete={handleDownloadComplete}
                  onError={handleError}
                />
              );
            })}
          </div>

          <div className="px-3 py-2 bg-muted/30 border-t border-border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>查询完成后可下载全部格式</span>
            </div>
          </div>

          <div className="px-3 py-2 bg-emerald-500/5 border-t border-border">
            <div className="text-xs text-emerald-700 dark:text-emerald-400 space-y-1">
              <div className="flex items-center gap-1.5 font-medium">
                <span>📁</span>
                <span>本地数据存储</span>
              </div>
              <div className="pl-4 space-y-0.5 text-muted-foreground">
                <div className="font-mono text-[10px] break-all">sources/{drugName.replace(/[^a-zA-Z0-9_\-\u4e00-\u9fff]/g, "_")}/</div>
                <div className="text-[10px]">数据已自动保存至本地 sources/ 目录</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
